import {
  Checkbox,
  DialogContent,
  FormControl,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { styled } from '@mui/styles';
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Cell } from 'react-table';
import {
  ControlledTextInput,
  Label,
  WsButton,
  WsColors,
  WsDataTable,
  WsDialog,
  WsSelect,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import {
  DeleteIconButton,
  EditIconButton,
  SaveIconButton,
  ArchiveIconButton,
  UnarchiveIconButton,
} from '../components/StyledIconButtons';
import DeleteConfirmationDialog from '../customizeStageStatus/DeleteConfirmation';
import { stageStatusPipelineType, statusesType } from '../customizeStageStatus/helper';

import { StyledBox } from './HeaderBox';

interface IStatusTypographyProps {
  cell: boolean;
}

const bgcolorStatusMap = new Map();

bgcolorStatusMap.set(false, WsColors.Green100);
bgcolorStatusMap.set(true, WsColors.Yellow100);

const colorStatusMap = new Map();

colorStatusMap.set(false, WsColors.Green500);
colorStatusMap.set(true, WsColors.Yellow500);

const StatusTypography = styled(Typography)(({ cell }: IStatusTypographyProps) => ({
  backgroundColor: bgcolorStatusMap.get(cell),
  color: colorStatusMap.get(cell),
  padding: 4,
  width: 'fit-content',
  borderRadius: 4,
}));

const BoldLabel = styled(Label)(() => ({
  fontWeight: 'bold',
}));

const EditWindowHeader = ({ setEditWindow, stageData, setEditStage, addNewStatus }) => (
  <StyledBox mb={5}>
    <Grid container>
      <Grid item xs={9.5} justifyContent="center">
        <WsTypography
          variant={WsTypoVariants.TITLE}
          bold
        >{`Add or Edit Statuses for ${stageData.stage}`}</WsTypography>
      </Grid>
      <Grid item xs={2.5} display="flex" justifyContent="space-between">
        <WsButton onClick={() => addNewStatus(-1)}>Add New</WsButton>
        <WsButton
          onClick={() => {
            setEditWindow(false);
            setEditStage(-1);
          }}
        >
          Close
        </WsButton>
      </Grid>
    </Grid>
  </StyledBox>
);

type StageEdit = {
  editWindow: boolean;
  setEditWindow: Dispatch<SetStateAction<boolean>>;
  setEditStage: Dispatch<SetStateAction<number>>;
  stageData: stageStatusPipelineType;
  setStatusData: (newStatus: statusesType[]) => void;
};

const fields = [
  'Stage Date',
  'Stage Date & Time',
  'Comments',
  'Events',
  'Other Documents',
  'Joining Date',
  'Offer Payrate',
];

const StageStatusEditWindow = ({
  editWindow,
  setEditWindow,
  stageData,
  setStatusData,
  setEditStage,
}: StageEdit) => {
  const [statuses] = useState(stageData.statuses);
  const [editItem, setEditItem] = useState(-1);
  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);
  const [stageStatus, setStageStatus] = useState<string[]>(['Comments', 'Events']);
  const [isDelete, setIsDelete] = useState<boolean>(false);
  const methods = useForm();

  const addNewStatus = useCallback(
    (statusIndex) => {
      statuses.splice(statusIndex + 1, 0, { value: 'New Status', status: '', mandatoryFields: [] });
      setStatusData([...statuses]);
    },
    [setStatusData, statuses]
  );

  const onStatusEditSave = useCallback(
    (data) => {
      data.editStatusName && (statuses[editItem].value = data.editStatusName);
      data.status && (statuses[editItem].status = data.status);
      setStatusData([...statuses]);
      setEditItem(-1);
      methods.reset();
    },
    [editItem, methods, setStatusData, statuses]
  );

  const onStatusDelete = (statusIndex) => {
    statuses.splice(statusIndex, 1);
    setStatusData([...statuses]);
    setIsDelete(false);
    setEditItem(-1);
  };

  const restoreStage = useCallback(
    (stageIndex, restoreStage, restoreArchive) => {
      if (statuses) {
        statuses.splice(restoreArchive, 1);
        statuses.splice(stageIndex + 1, 0, { ...restoreStage, isArchive: false });
        setStatusData([...statuses]);
      }
    },
    [setStatusData, statuses]
  );

  const onArchive = useCallback(
    (archive: boolean, restoreArchive: number) => {
      if (statuses && archive) {
        const archiveStage = statuses[restoreArchive];
        statuses.splice(restoreArchive, 1);
        archiveStage.isArchive = archive;
        statuses.push(archiveStage);
        setStatusData([...statuses]);
      } else if (statuses) {
        restoreStage(
          statuses.filter((element) => !element.isArchive).length - 1,
          statuses[restoreArchive],
          restoreArchive
        );
      }
      setEditItem(-1);
      setOpenConfirmDialog(false);
      setIsDelete(false);
      methods.reset();
    },
    [methods, restoreStage, setStatusData, statuses]
  );

  const handleSelectChange = useCallback(
    (event: SelectChangeEvent<typeof stageStatus>, item: number) => {
      const {
        target: { value },
      } = event;
      const stageStatusArray: string[] = typeof value === 'string' ? value.split(',') : value;
      setStageStatus(stageStatusArray);
      if (statuses) {
        const status = statuses[item];
        status.mandatoryFields = stageStatusArray;
        setStatusData([...statuses]);
      }
      console.log(statuses);
      setOpenConfirmDialog(false);
      setIsDelete(false);
    },
    [setStatusData, statuses]
  );

  const statuesListData = useMemo(
    () =>
      stageData.statuses.map((e) => ({
        statusName: (
          <FormProvider {...methods}>
            <Grid item container>
              {editItem === statuses.indexOf(e) ? (
                <>
                  <Grid item xs={9}>
                    <ControlledTextInput
                      name="editStatusName"
                      defaultValue={e.value}
                      size="small"
                      inputProps={{ padding: '9px 14px' }}
                    />
                  </Grid>
                </>
              ) : (
                <BoldLabel>{`${e.value}`}</BoldLabel>
              )}
            </Grid>
          </FormProvider>
        ),
        type: (
          <FormProvider {...methods}>
            <Grid item container>
              {editItem === statuses.indexOf(e) ? (
                <>
                  <Grid item xs={12}>
                    <WsSelect
                      name="status"
                      variant="outlined"
                      size="small"
                      selectArray={[
                        { label: 'Active', value: 'active' },
                        { label: 'Close', value: 'close' },
                      ]}
                    />
                  </Grid>
                </>
              ) : e.status === 'active' ? (
                'Active'
              ) : (
                'Close'
              )}
            </Grid>
          </FormProvider>
        ),
        isArchived: (
          <StatusTypography cell={e.isArchive ?? false}>
            {e.isArchive ? 'Archived' : 'Available'}
          </StatusTypography>
        ),
      })),
    [editItem, methods, stageData.statuses, statuses]
  );

  const columns = useMemo(
    () => [
      {
        Header: 'Status Name',
        accessor: 'statusName',
      },
      {
        Header: '',
        accessor: 'isArchived',
      },
      {
        Header: 'Type',
        accessor: 'type',
      },
      {
        Header: 'Mandatory Fields',
        accessor: 'mandatoryFields',
        Cell: (cell: Cell) => {
          return (
            <Grid item>
              {editItem === cell.row.index ? (
                <FormControl sx={{ width: 300 }}>
                  <InputLabel>Fields</InputLabel>
                  <Select
                    multiple
                    value={statuses[cell.row.index].mandatoryFields}
                    onChange={(event) => {
                      handleSelectChange(event, cell.row.index);
                    }}
                    input={<OutlinedInput label="Stage" />}
                    renderValue={(selected) => selected.join(', ')}
                  >
                    {fields.map((name) => (
                      <MenuItem key={name} value={name}>
                        <Checkbox
                          checked={statuses[cell.row.index].mandatoryFields?.includes(name)}
                        />
                        <ListItemText primary={name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <WsTypography
                  cursor="pointer"
                  hoverText={(statuses[cell.row.index]?.mandatoryFields ?? []).join(', ')}
                >
                  {(statuses[cell.row.index]?.mandatoryFields ?? []).length > 3
                    ? `${(statuses[cell.row.index]?.mandatoryFields ?? [])
                        .slice(0, 3)
                        .join(', ')} ...`
                    : statuses[cell.row.index]?.mandatoryFields?.length === 0
                    ? ''
                    : (statuses[cell.row.index]?.mandatoryFields ?? []).join(', ')}
                </WsTypography>
              )}
            </Grid>
          );
        },
      },
      {
        Header: 'Actions',
        accessor: 'actions',
        Cell: (cell: Cell) => {
          return statuses[cell.row.index].value != 'Joined' ? (
            <Grid item xs={12} container>
              {!statuses[cell.row.index].isArchive ? (
                <ArchiveIconButton
                  onClick={() => {
                    setEditItem(cell.row.index);
                    onArchive(true, cell.row.index);
                  }}
                />
              ) : (
                <UnarchiveIconButton onClick={() => onArchive(false, cell.row.index)} />
              )}
              {editItem === cell.row.index && !openConfirmDialog && !isDelete ? (
                <SaveIconButton onClick={methods.handleSubmit(onStatusEditSave)} />
              ) : (
                <EditIconButton
                  onClick={() => {
                    setEditItem(cell.row.index);
                    setOpenConfirmDialog(true);
                  }}
                />
              )}

              <DeleteIconButton
                onClick={() => {
                  setEditItem(cell.row.index);
                  setIsDelete(true);
                  setOpenConfirmDialog(true);
                }}
              />
            </Grid>
          ) : (
            <></>
          );
        },
      },
    ],
    [
      editItem,
      handleSelectChange,
      isDelete,
      methods,
      onArchive,
      onStatusEditSave,
      openConfirmDialog,
      statuses,
    ]
  );

  return (
    <WsDialog openDialog={editWindow} size="large">
      <DialogContent>
        <EditWindowHeader
          setEditWindow={setEditWindow}
          stageData={stageData}
          setEditStage={setEditStage}
          addNewStatus={addNewStatus}
        />
        {editItem !== -1 && openConfirmDialog && (
          <DeleteConfirmationDialog
            openDialog={openConfirmDialog}
            isEdit={editItem !== -1 && !isDelete}
            closeDialog={() => {
              setEditItem(-1);
              setIsDelete(false);
            }}
            onArchive={(value) => onArchive(value, editItem)}
            handler={(isEditStatus) => {
              if (!isEditStatus) {
                onStatusDelete(editItem);
              }
              setOpenConfirmDialog(false);
            }}
          />
        )}
        <WsDataTable
          columns={columns}
          data={statuesListData}
          totalDataItems={10}
          selectable={false}
          boldItems={['name', 'invite']}
          pageable={false}
        />
      </DialogContent>
    </WsDialog>
  );
};

export default StageStatusEditWindow;
